import { AbsoluteFill, interpolate, useCurrentFrame } from "remotion";
import {
  Caption,
  Callout,
  CodePanel,
  COLORS,
  FONTS,
  MetricCounter,
  Panel,
  PipelineGraph,
  SceneTransition,
  SlideTimeBar,
  type PipeNode,
} from "../../coldcompute";
import { SCENE } from "./constants";
import { HOOK, PROBLEM, INTUITION, ROUTE, PIPELINE, CODE, PAYOFF, OUTRO } from "./script";

const center: React.CSSProperties = {
  alignItems: "center",
  justifyContent: "center",
  flexDirection: "column",
};

const Kicker: React.FC<{ text: string; color: string }> = ({ text, color }) => (
  <div
    style={{
      fontFamily: FONTS.mono,
      fontSize: 22,
      letterSpacing: "0.22em",
      textTransform: "uppercase",
      color,
      marginBottom: 22,
    }}
  >
    ▍{text}
  </div>
);

const Big: React.FC<{ children: React.ReactNode; size?: number }> = ({
  children,
  size = 92,
}) => (
  <div
    style={{
      fontFamily: FONTS.display,
      fontWeight: 700,
      fontSize: size,
      lineHeight: 0.98,
      letterSpacing: "-0.02em",
      color: COLORS.text,
      textAlign: "center",
      maxWidth: 1500,
    }}
  >
    {children}
  </div>
);

/* 01 — HOOK */
export const Scene01Hook: React.FC = () => {
  const frame = useCurrentFrame();
  const flip = interpolate(frame, [44, 74], [0, 1], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });
  return (
    <AbsoluteFill style={center}>
      <SlideTimeBar sceneDurationFrames={SCENE.hook.duration} />
      <SceneTransition from="up">
        <AbsoluteFill style={center}>
          <Kicker text="smart_process · one workflow" color={COLORS.cyan} />
          <Big>
            it picks its{" "}
            <span style={{ color: COLORS.cyan }}>own</span> model
            <br />
            <span style={{ fontSize: 56, color: COLORS.dim }}>
              cheap when small ·{" "}
              <span style={{ color: COLORS.lime, opacity: 0.4 + flip * 0.6 }}>
                strong when big
              </span>
            </span>
          </Big>
        </AbsoluteFill>
      </SceneTransition>
      <Caption lines={HOOK} accent="cyan" />
    </AbsoluteFill>
  );
};

/* 02 — PROBLEM */
export const Scene02Problem: React.FC = () => (
  <AbsoluteFill style={center}>
    <SlideTimeBar sceneDurationFrames={SCENE.problem.duration} />
    <SceneTransition from="up">
      <AbsoluteFill style={center}>
        <Kicker text="the default move" color={COLORS.amber} />
        <Panel accent="amber" title="one giant call · whole input" width={760}>
          <div
            style={{
              fontFamily: FONTS.body,
              fontSize: 30,
              lineHeight: 1.75,
              color: COLORS.text,
            }}
          >
            <div>
              <span style={{ color: COLORS.amber }}>cost</span> rises with the
              context
            </div>
            <div>
              <span style={{ color: COLORS.amber }}>latency</span> rises with the
              context
            </div>
            <div>
              quality drops —{" "}
              <span style={{ color: COLORS.red }}>lost in the middle</span>
            </div>
          </div>
        </Panel>
        <div
          style={{
            fontFamily: FONTS.mono,
            fontSize: 22,
            color: COLORS.dim,
            marginTop: 26,
          }}
        >
          a bigger thinking model just pays over the problem
        </div>
      </AbsoluteFill>
    </SceneTransition>
    <Caption lines={PROBLEM} accent="amber" />
  </AbsoluteFill>
);

/* 03 — INTUITION */
export const Scene03Intuition: React.FC = () => (
  <AbsoluteFill style={center}>
    <SlideTimeBar sceneDurationFrames={SCENE.intuition.duration} />
    <SceneTransition from="up">
      <AbsoluteFill style={center}>
        <Kicker text="intuition" color={COLORS.violet} />
        <Big size={70}>
          depth comes from <span style={{ color: COLORS.violet }}>architecture</span>
          <br />
          not a bigger model
        </Big>
        <div
          style={{
            display: "flex",
            gap: 30,
            marginTop: 40,
            fontFamily: FONTS.mono,
            fontSize: 26,
            color: COLORS.dim,
          }}
        >
          <span>
            small → <span style={{ color: COLORS.cyan }}>ask once</span>
          </span>
          <span style={{ color: COLORS.line }}>|</span>
          <span>
            big → <span style={{ color: COLORS.lime }}>split, then fuse</span>
          </span>
        </div>
      </AbsoluteFill>
    </SceneTransition>
    <Caption lines={INTUITION} accent="violet" />
  </AbsoluteFill>
);

/* 04 — ROUTE (the deterministic threshold) */
export const Scene04Route: React.FC = () => {
  const frame = useCurrentFrame();
  const a = interpolate(frame, [20, 50], [0, 1], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
  const b = interpolate(frame, [60, 90], [0, 1], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
  return (
    <AbsoluteFill style={center}>
      <SlideTimeBar sceneDurationFrames={SCENE.route.duration} />
      <AbsoluteFill style={center}>
        <Kicker text="decideRoute · no router LLM" color={COLORS.green} />
        <div style={{ display: "flex", gap: 28, alignItems: "stretch" }}>
          <div style={{ opacity: a }}>
            <Panel accent="cyan" title="SINGLE · one call" width={480}>
              <div style={{ fontFamily: FONTS.mono, fontSize: 22, lineHeight: 1.9, color: COLORS.text }}>
                <div>estTokens ≤ <b style={{ color: COLORS.cyan }}>6000</b></div>
                <div>AND items ≤ <b style={{ color: COLORS.cyan }}>8</b></div>
                <div style={{ color: COLORS.dim }}>→ byte-identical to "just ask"</div>
              </div>
            </Panel>
          </div>
          <div style={{ opacity: b }}>
            <Panel accent="lime" title="SPLIT · map + reduce" width={480}>
              <div style={{ fontFamily: FONTS.mono, fontSize: 22, lineHeight: 1.9, color: COLORS.text }}>
                <div>estTokens &gt; <b style={{ color: COLORS.lime }}>6000</b></div>
                <div>OR items &gt; <b style={{ color: COLORS.lime }}>8</b></div>
                <div style={{ color: COLORS.dim }}>→ fan out, then fuse</div>
              </div>
            </Panel>
          </div>
        </div>
        <div style={{ fontFamily: FONTS.body, fontSize: 24, color: COLORS.dim, marginTop: 30 }}>
          thresholds are code constants — never a user-facing flag
        </div>
      </AbsoluteFill>
      <Caption lines={ROUTE} accent="green" />
    </AbsoluteFill>
  );
};

/* 05 — PIPELINE */
const PIPE: PipeNode[] = [
  { label: "input", value: "BIG", sub: "> threshold", accent: "amber" },
  { label: "split", value: "CHUNKS", sub: "≤3000 tok", accent: "cyan" },
  { label: "map · parallel", value: "FLASH ×N", sub: "cheap tier", accent: "green" },
  { label: "reduce · 1", value: "2.5 PRO", sub: "strong tier", accent: "lime" },
];
export const Scene05Pipeline: React.FC = () => (
  <AbsoluteFill style={center}>
    <SlideTimeBar sceneDurationFrames={SCENE.pipeline.duration} />
    <AbsoluteFill style={{ ...center, padding: "0 80px" }}>
      <Kicker text="split → map → reduce" color={COLORS.green} />
      <div style={{ width: 1680 }}>
        <PipelineGraph nodes={PIPE} startFrame={6} stepFrames={24} nodeWidth={230} />
      </div>
      <div
        style={{
          fontFamily: FONTS.mono,
          fontSize: 22,
          color: COLORS.dim,
          marginTop: 40,
        }}
      >
        each map output + a full sub-step trace persist to the blackboard
      </div>
    </AbsoluteFill>
    <Caption lines={PIPELINE} accent="green" />
  </AbsoluteFill>
);

/* 06 — CODE */
const TS = `// src/lib/llm/map-reduce/index.ts
export function decideRoute(items, opts = {}) {
  const tokens = estimateTokens(items);   // chars / 4
  if (items.length > 8)   return { mode: 'split' };
  if (tokens > 6000)      return { mode: 'split' };
  return { mode: 'single' };               // one strong call
}

// smart_process wires the tiers (src/lib/workflows/smart-process)
const mapModel    = await getModelForModeAsync('generation'); // Flash
const reduceModel = await getModelForModeAsync('deep');        // 2.5 Pro

// every map chunk fails? throw — never mark the step "done"
if (okCount === 0) throw new Error('all map calls failed');`;
export const Scene06Code: React.FC = () => (
  <AbsoluteFill style={center}>
    <SlideTimeBar sceneDurationFrames={SCENE.code.duration} />
    <AbsoluteFill style={{ alignItems: "center", justifyContent: "center", paddingBottom: 40 }}>
      <CodePanel
        code={TS}
        language="typescript"
        filename="map-reduce/index.ts + smart-process/index.ts"
        highlightLines={[4, 5, 6, 12, 13]}
        revealFrames={360}
        fontSize={23}
        width={1320}
      />
    </AbsoluteFill>
    <Caption lines={CODE} accent="green" />
  </AbsoluteFill>
);

/* 07 — PAYOFF */
export const Scene07Payoff: React.FC = () => {
  const frame = useCurrentFrame();
  const c1 = interpolate(frame, [110, 140], [0, 1], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
  const c2 = interpolate(frame, [210, 240], [0, 1], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
  return (
    <AbsoluteFill style={center}>
      <SlideTimeBar sceneDurationFrames={SCENE.payoff.duration} />
      <AbsoluteFill style={center}>
        <Kicker text="cheap map ×N  +  strong reduce ×1" color={COLORS.lime} />
        <div style={{ display: "flex", alignItems: "baseline", gap: 20 }}>
          <MetricCounter value={1} startFrame={10} durationFrames={50} accent="lime" prefix="×" fontSize={150} />
          <span style={{ fontFamily: FONTS.display, fontWeight: 700, fontSize: 60, color: COLORS.dim }}>
            strong reduce
          </span>
        </div>
        <div style={{ display: "flex", gap: 30, marginTop: 38 }}>
          <div style={{ opacity: c1 }}>
            <Callout label="Mixture-of-Agents · 2406.04692" accent="green" width={380}>
              cheap parallel proposers + 1 strong aggregator{" "}
              <b style={{ color: COLORS.text }}>beat a single expensive model</b>
            </Callout>
          </div>
          <div style={{ opacity: c2 }}>
            <Callout label="invisible by design" accent="lime" width={380}>
              the step just says <b style={{ color: COLORS.text }}>"compare and merge"</b> — no flag, no toggle
            </Callout>
          </div>
        </div>
      </AbsoluteFill>
      <Caption lines={PAYOFF} accent="lime" />
    </AbsoluteFill>
  );
};

/* 08 — OUTRO */
export const Scene08Outro: React.FC = () => (
  <AbsoluteFill style={center}>
    <SlideTimeBar sceneDurationFrames={SCENE.outro.duration} />
    <SceneTransition from="up">
      <AbsoluteFill style={center}>
        <Big size={70}>
          Don't reach for a bigger model.
          <br />
          <span style={{ color: COLORS.cyan }}>Reach for a better shape.</span>
        </Big>
        <div style={{ fontFamily: FONTS.mono, fontSize: 24, letterSpacing: "0.24em", color: COLORS.dim, marginTop: 44 }}>
          EAKC · smart_process · auto-split map-reduce
        </div>
      </AbsoluteFill>
    </SceneTransition>
    <Caption lines={OUTRO} accent="cyan" />
  </AbsoluteFill>
);

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
